import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import {
  useAssignSettingSelfServeBrandInstantAnswers,
  useSettingSelfServeBrandInstantAnswers,
} from "@faire/help-center-utils/settings/useSettingSelfServiceBrandInstantAnswers";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Clipboard } from "@faire/web--source/slate/icons/foundation/Clipboard";
import React from "react";

import { SelfServeNode, SelfServeNodes } from "../../types";

export const useMyCatalogBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const enableSettingSelfServeBrandInstantAnswers =
    useSettingSelfServeBrandInstantAnswers();
  const assignSettingSelfServeBrandInstantAnswers =
    useAssignSettingSelfServeBrandInstantAnswers();
  const articles = useArticles();

  const commonFields: SelfServeNode = {
    id: "MyCatalog",
    label: strictLocalize({
      defaultMessage: "Product catalog",
      description: {
        text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
      },
    }),
    icon: <Clipboard fontSize="24px" color="faireBlack" />,
    articles: [
      articles.ManageMyShop.CUSTOMIZE_MY_PRODUCT_TRANSLATIONS,
      articles.ManageMyShop.CURRENCY_SPECIFIC_PRICING,
      articles.ManageMyShop.SETTING_PRODUCT_INVENTORY_LEVELS,
      articles.ManageMyShop.ARRANGE_MY_SHOP_LISTINGS,
    ],
    level: 1,
    assignSetting: assignSettingSelfServeBrandInstantAnswers,
    sidebarTitle: strictLocalize({
      defaultMessage: "Catalog issue",
      description: {
        text: "Title for displaying the issue on the order summary sidebar",
      },
    }),
  };

  if (!enableSettingSelfServeBrandInstantAnswers) {
    return {
      MyCatalog: {
        ...commonFields,
      },
    };
  }

  return {
    MyCatalog: {
      ...commonFields,
      children: [
        "AllProducts",
        "GetFaireToHelpAddProducts",
        "EditProducts",
        "ListingSettings",
        "InternationalPricing",
      ],
      shouldStopAddingDefaultOtherNodeToChildren: true,
    },
    AllProducts: {
      id: "AllProducts",
      label: strictLocalize({
        defaultMessage: "All products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "AddSingleProduct",
        "AddMultipleProducts",
        "ImportProductsWithIntegrations",
        "AllowedAndRestrictedProducts",
      ],
      level: 2,
    },
    GetFaireToHelpAddProducts: {
      id: "GetFaireToHelpAddProducts",
      label: strictLocalize({
        defaultMessage: "Get Faire to help add products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    EditProducts: {
      id: "EditProducts",
      label: strictLocalize({
        defaultMessage: "Edit products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "EditSingleProduct",
        "EditMultipleProducts",
        "ManageProductImages",
      ],
      level: 2,
    },
    ListingSettings: {
      id: "ListingSettings",
      label: strictLocalize({
        defaultMessage: "Listing settings",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "Preorders",
        "OpenSizing",
        "Prepacks",
        "TariffCodes",
        "ProductTranslations",
        "Samples",
      ],
      level: 2,
    },
    InternationalPricing: {
      id: "InternationalPricing",
      label: strictLocalize({
        defaultMessage: "International pricing",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    AddSingleProduct: {
      id: "AddSingleProduct",
      label: strictLocalize({
        defaultMessage: "Add a single product",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    AddMultipleProducts: {
      id: "AddMultipleProducts",
      label: strictLocalize({
        defaultMessage: "Add multiple products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ImportProductsWithIntegrations: {
      id: "ImportProductsWithIntegrations",
      label: strictLocalize({
        defaultMessage: "Import products with integrations",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    AllowedAndRestrictedProducts: {
      id: "AllowedAndRestrictedProducts",
      label: strictLocalize({
        defaultMessage: "Allowed & restricted products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    EditSingleProduct: {
      id: "EditSingleProduct",
      label: strictLocalize({
        defaultMessage: "Edit a single product",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    EditMultipleProducts: {
      id: "EditMultipleProducts",
      label: strictLocalize({
        defaultMessage: "Edit multiple products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ManageProductImages: {
      id: "ManageProductImages",
      label: strictLocalize({
        defaultMessage: "Manage product images",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    Preorders: {
      id: "Preorders",
      label: strictLocalize({
        defaultMessage: "Pre-orders",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    OpenSizing: {
      id: "OpenSizing",
      label: strictLocalize({
        defaultMessage: "Open sizing",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    Prepacks: {
      id: "Prepacks",
      label: strictLocalize({
        defaultMessage: "Prepacks",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    TariffCodes: {
      id: "TariffCodes",
      label: strictLocalize({
        defaultMessage: "Tariff codes",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    ProductTranslations: {
      id: "ProductTranslations",
      label: strictLocalize({
        defaultMessage: "Product translations",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
    Samples: {
      id: "Samples",
      label: strictLocalize({
        defaultMessage: "Samples",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
    },
  };
};
